import { useState, useContext, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  alpha,
  Chip,
  IconButton,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import GridViewIcon from '@mui/icons-material/GridView';
import InfoIcon from '@mui/icons-material/Info';
import { ColorModeContext } from '../../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../../theme/colors';
import { useBulkCreateVariants } from '../../../hooks/useVariants';
import { useSnackbar } from '../../../components/SnackbarProvider';
import type { Product, BulkCreateVariantsDto } from '../../../types';

interface BulkVariantCreationDialogProps {
  open: boolean;
  onClose: () => void;
  product: Product;
}

interface AttributeInput {
  name: string;
  values: string[];
}

interface BulkVariantFormData {
  skuTemplate: string;
}

export const BulkVariantCreationDialog = ({
  open,
  onClose,
  product,
}: BulkVariantCreationDialogProps) => {
  const { showMessage } = useSnackbar();
  const { mode } = useContext(ColorModeContext);
  const [attributes, setAttributes] = useState<AttributeInput[]>([
    { name: 'size', values: [] },
    { name: 'color', values: [] },
  ]);
  const [newAttributeName, setNewAttributeName] = useState('');
  const [attributeValueInputs, setAttributeValueInputs] = useState<Record<string, string>>({});

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<BulkVariantFormData>({
    defaultValues: {
      skuTemplate: '{productSKU}-{size}-{color}',
    },
  });

  const skuTemplate = watch('skuTemplate');

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const bulkCreateMutation = useBulkCreateVariants(product.id);

  // Calculate variant preview
  const variantPreview = useMemo(() => {
    const activeAttributes = attributes.filter(attr => attr.values.length > 0);

    if (activeAttributes.length === 0) {
      return [];
    }

    // Generate all combinations using cartesian product
    const generateCombinations = (attrs: AttributeInput[]): Record<string, string>[] => {
      if (attrs.length === 0) return [];
      if (attrs.length === 1) {
        return attrs[0].values.map(value => ({ [attrs[0].name]: value }));
      }

      const [first, ...rest] = attrs;
      const restCombinations = generateCombinations(rest);
      const result: Record<string, string>[] = [];

      for (const value of first.values) {
        if (restCombinations.length === 0) {
          result.push({ [first.name]: value });
        } else {
          for (const combo of restCombinations) {
            result.push({ [first.name]: value, ...combo });
          }
        }
      }

      return result;
    };

    const combinations = generateCombinations(activeAttributes);

    // Generate SKU preview for each combination
    return combinations.map(combo => {
      let sku = skuTemplate;

      // Replace {productSKU} placeholder
      sku = sku.replace(/{productSKU}/g, product.sku || 'PROD');

      // Replace attribute placeholders
      Object.entries(combo).forEach(([key, value]) => {
        sku = sku.replace(new RegExp(`{${key}}`, 'g'), value);
      });

      return {
        attributes: combo,
        sku,
      };
    });
  }, [attributes, skuTemplate, product.sku]);

  const handleAddAttribute = () => {
    if (!newAttributeName.trim()) return;

    // Check if attribute already exists
    if (attributes.some(attr => attr.name.toLowerCase() === newAttributeName.toLowerCase())) {
      showMessage('Attribute already exists', 'warning');
      return;
    }

    setAttributes([...attributes, { name: newAttributeName.trim().toLowerCase(), values: [] }]);
    setNewAttributeName('');
  };

  const handleRemoveAttribute = (index: number) => {
    setAttributes(attributes.filter((_, i) => i !== index));
  };

  const handleAddAttributeValue = (attributeName: string) => {
    const value = attributeValueInputs[attributeName]?.trim();
    if (!value) return;

    setAttributes(attributes.map(attr => {
      if (attr.name === attributeName) {
        // Check if value already exists
        if (attr.values.includes(value)) {
          showMessage(`Value "${value}" already exists`, 'warning');
          return attr;
        }
        return { ...attr, values: [...attr.values, value] };
      }
      return attr;
    }));

    setAttributeValueInputs({ ...attributeValueInputs, [attributeName]: '' });
  };

  const handleRemoveAttributeValue = (attributeName: string, value: string) => {
    setAttributes(attributes.map(attr => {
      if (attr.name === attributeName) {
        return { ...attr, values: attr.values.filter(v => v !== value) };
      }
      return attr;
    }));
  };

  const handleAttributeValueKeyPress = (e: React.KeyboardEvent, attributeName: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddAttributeValue(attributeName);
    }
  };

  const onSubmit = (data: BulkVariantFormData) => {
    // Validate that we have at least one attribute with values
    const activeAttributes = attributes.filter(attr => attr.values.length > 0);

    if (activeAttributes.length === 0) {
      showMessage('Please add at least one attribute with values', 'warning');
      return;
    }

    // Build the bulk create request
    const requestData: BulkCreateVariantsDto = {
      attributes: activeAttributes.reduce((acc, attr) => {
        acc[attr.name] = attr.values;
        return acc;
      }, {} as Record<string, string[]>),
      skuTemplate: data.skuTemplate,
    };

    bulkCreateMutation.mutate(requestData, {
      onSuccess: (variants) => {
        showMessage(`Successfully created ${variants.length} variants`, 'success');
        handleClose();
      },
      onError: (error: any) => {
        showMessage(
          error.response?.data?.message || 'Failed to create variants',
          'error'
        );
      },
    });
  };

  const handleClose = () => {
    if (!bulkCreateMutation.isPending) {
      reset();
      setAttributes([
        { name: 'size', values: [] },
        { name: 'color', values: [] },
      ]);
      setAttributeValueInputs({});
      setNewAttributeName('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.surface,
          border: `2px solid ${palette.primary}`,
          borderRadius: theme.radius.md,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: typography.fontFamily.display,
          fontWeight: typography.fontWeight.bold,
          color: colors.textPrimary,
          textTransform: 'uppercase',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <GridViewIcon sx={{ color: palette.primary }} />
        Bulk Variant Creation
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box component="form" noValidate>
          {/* Product Info */}
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: theme.radius.sm,
              backgroundColor: alpha(palette.primary, 0.05),
              border: `1px solid ${colors.border}`,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
                color: colors.textPrimary,
                fontWeight: typography.fontWeight.semibold,
              }}
            >
              Product: {product.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                color: colors.textMuted,
              }}
            >
              Base SKU: {product.sku} | Base Price: ${product.price.toFixed(2)}
            </Typography>
          </Box>

          {/* Attributes Section */}
          <Typography
            variant="subtitle2"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontWeight: typography.fontWeight.bold,
              color: colors.textPrimary,
              textTransform: 'uppercase',
              mb: 2,
            }}
          >
            Product Attributes
          </Typography>

          {attributes.map((attribute, index) => (
            <Box
              key={index}
              sx={{
                mb: 2,
                p: 2,
                border: `1px solid ${colors.border}`,
                borderRadius: theme.radius.sm,
                backgroundColor: colors.surfaceElevated,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.semibold,
                    color: colors.textPrimary,
                    textTransform: 'capitalize',
                  }}
                >
                  {attribute.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveAttribute(index)}
                  sx={{ color: palette.error }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                {attribute.values.map((value, valueIndex) => (
                  <Chip
                    key={valueIndex}
                    label={value}
                    onDelete={() => handleRemoveAttributeValue(attribute.name, value)}
                    size="small"
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      backgroundColor: alpha(palette.primary, 0.1),
                      color: palette.primary,
                      border: `1px solid ${palette.primary}`,
                    }}
                  />
                ))}
              </Box>

              <TextField
                size="small"
                fullWidth
                placeholder={`Add ${attribute.name} value (press Enter)`}
                value={attributeValueInputs[attribute.name] || ''}
                onChange={(e) =>
                  setAttributeValueInputs({
                    ...attributeValueInputs,
                    [attribute.name]: e.target.value,
                  })
                }
                onKeyPress={(e) => handleAttributeValueKeyPress(e, attribute.name)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => handleAddAttributeValue(attribute.name)}
                        disabled={!attributeValueInputs[attribute.name]?.trim()}
                        sx={{ color: palette.primary }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: colors.surface,
                  },
                }}
              />
            </Box>
          ))}

          {/* Add New Attribute */}
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="New attribute name (e.g., material, style)"
              value={newAttributeName}
              onChange={(e) => setNewAttributeName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddAttribute();
                }
              }}
            />
            <Button
              onClick={handleAddAttribute}
              disabled={!newAttributeName.trim()}
              startIcon={<AddIcon />}
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontWeight: typography.fontWeight.bold,
                textTransform: 'uppercase',
                fontSize: typography.fontSize.xs,
                letterSpacing: typography.letterSpacing.wide,
                color: palette.primary,
                border: `1px solid ${palette.primary}`,
                borderRadius: theme.radius.xs,
                '&:hover': {
                  backgroundColor: alpha(palette.primary, 0.1),
                },
              }}
            >
              Add
            </Button>
          </Box>

          <Divider sx={{ my: 3, borderColor: colors.border }} />

          {/* SKU Template */}
          <Typography
            variant="subtitle2"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontWeight: typography.fontWeight.bold,
              color: colors.textPrimary,
              textTransform: 'uppercase',
              mb: 2,
            }}
          >
            SKU Template
          </Typography>

          <Controller
            name="skuTemplate"
            control={control}
            rules={{
              required: 'SKU template is required',
              validate: (value) => {
                if (!value.includes('{productSKU}')) {
                  return 'Template must include {productSKU} placeholder';
                }
                return true;
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                error={!!errors.skuTemplate}
                helperText={
                  errors.skuTemplate?.message ||
                  'Use placeholders like {productSKU}, {size}, {color}, etc.'
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title="Available placeholders: {productSKU} and any attribute name in curly braces"
                        arrow
                      >
                        <InfoIcon sx={{ color: colors.textMuted }} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    fontFamily: typography.fontFamily.mono,
                  },
                }}
              />
            )}
          />

          {/* Variant Preview */}
          {variantPreview.length > 0 && (
            <>
              <Divider sx={{ my: 3, borderColor: colors.border }} />

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontFamily: typography.fontFamily.display,
                    fontWeight: typography.fontWeight.bold,
                    color: colors.textPrimary,
                    textTransform: 'uppercase',
                  }}
                >
                  Preview
                </Typography>
                <Chip
                  label={`${variantPreview.length} variants will be created`}
                  size="small"
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontWeight: typography.fontWeight.bold,
                    backgroundColor: alpha(palette.success, 0.1),
                    color: palette.success,
                    border: `1px solid ${palette.success}`,
                  }}
                />
              </Box>

              {variantPreview.length > 10 ? (
                <Alert
                  severity="info"
                  sx={{
                    backgroundColor: alpha(palette.primary, 0.05),
                    color: colors.textPrimary,
                    border: `1px solid ${palette.primary}`,
                  }}
                >
                  {variantPreview.length} variants will be created. Showing first 10 in preview.
                </Alert>
              ) : null}

              <TableContainer
                sx={{
                  maxHeight: 300,
                  border: `1px solid ${colors.border}`,
                  borderRadius: theme.radius.sm,
                  backgroundColor: colors.surfaceElevated,
                  mt: 2,
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor: alpha(palette.primary, 0.05),
                      }}
                    >
                      <TableCell
                        sx={{
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.bold,
                          textTransform: 'uppercase',
                          color: colors.textMuted,
                          backgroundColor: alpha(palette.primary, 0.05),
                        }}
                      >
                        SKU
                      </TableCell>
                      {attributes
                        .filter(attr => attr.values.length > 0)
                        .map((attr) => (
                          <TableCell
                            key={attr.name}
                            sx={{
                              fontFamily: typography.fontFamily.mono,
                              fontSize: typography.fontSize.xs,
                              fontWeight: typography.fontWeight.bold,
                              textTransform: 'uppercase',
                              color: colors.textMuted,
                              backgroundColor: alpha(palette.primary, 0.05),
                            }}
                          >
                            {attr.name}
                          </TableCell>
                        ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {variantPreview.slice(0, 10).map((variant, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:hover': {
                            backgroundColor: alpha(palette.primary, 0.02),
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            fontFamily: typography.fontFamily.mono,
                            fontSize: typography.fontSize.sm,
                            color: colors.textPrimary,
                          }}
                        >
                          {variant.sku}
                        </TableCell>
                        {attributes
                          .filter(attr => attr.values.length > 0)
                          .map((attr) => (
                            <TableCell
                              key={attr.name}
                              sx={{
                                fontFamily: typography.fontFamily.mono,
                                fontSize: typography.fontSize.sm,
                                color: colors.textSecondary,
                              }}
                            >
                              {variant.attributes[attr.name]}
                            </TableCell>
                          ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {variantPreview.length === 0 && (
            <Alert
              severity="info"
              sx={{
                backgroundColor: alpha(palette.primary, 0.05),
                color: colors.textPrimary,
                border: `1px solid ${palette.primary}`,
              }}
            >
              Add attribute values to see variant preview
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: `1px solid ${colors.border}`,
          backgroundColor: colors.surfaceElevated,
          px: 3,
          py: 2,
        }}
      >
        <Button
          onClick={handleClose}
          disabled={bulkCreateMutation.isPending}
          startIcon={<CancelIcon />}
          sx={{
            fontFamily: typography.fontFamily.mono,
            fontWeight: typography.fontWeight.bold,
            textTransform: 'uppercase',
            fontSize: typography.fontSize.xs,
            letterSpacing: typography.letterSpacing.wide,
            color: colors.textMuted,
            '&:hover': {
              backgroundColor: alpha(colors.textMuted, 0.1),
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={bulkCreateMutation.isPending || variantPreview.length === 0}
          startIcon={bulkCreateMutation.isPending ? <CircularProgress size={16} /> : <SaveIcon />}
          sx={{
            fontFamily: typography.fontFamily.mono,
            fontWeight: typography.fontWeight.bold,
            textTransform: 'uppercase',
            fontSize: typography.fontSize.xs,
            letterSpacing: typography.letterSpacing.wide,
            color: palette.primary,
            border: `1px solid ${palette.primary}`,
            borderRadius: theme.radius.xs,
            '&:hover': {
              backgroundColor: alpha(palette.primary, 0.1),
            },
            '&:disabled': {
              opacity: 0.6,
            },
          }}
        >
          {bulkCreateMutation.isPending ? 'Creating...' : `Create ${variantPreview.length} Variants`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
